import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  Loader2, Copy, RefreshCw, ShieldCheck, Shield, MoreVertical, UserMinus,
  UserPlus, LogOut, Users as UsersIcon, Settings as SettingsIcon, Check, X, Info, Ban,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  grupoService, Grupo, AtualizarGrupoData, SolicitacaoEntrada, META_LIMITE_PARTICIPANTES,
} from '../../services/grupoService';

interface GrupoDetalheDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grupoId: string | null;
  onAtualizado: (grupo: Grupo) => void;
  onSaiu: (grupoId: string) => void;
}

const parseParticipantes = (texto: string): string[] =>
  texto
    .split(/[\n,]/)
    .map((linha) => linha.replace(/\D/g, ''))
    .filter((numero) => numero.length >= 8);

const inferirDuracaoInicial = (grupo: Grupo): '0' | '1d' | '7d' | '90d' => {
  if (!grupo.isEphemeral || !grupo.disappearingTimer) return '0';
  if (grupo.disappearingTimer <= 86400) return '1d';
  if (grupo.disappearingTimer <= 604800) return '7d';
  return '90d';
};

const formatJid = (jid: string) => jid.replace('@s.whatsapp.net', '').replace('@g.us', '').replace('@lid', '');

const getIniciais = (nome?: string | null, jid?: string) => {
  const base = nome || formatJid(jid || '');
  return base.slice(0, 2).toUpperCase();
};

export const GrupoDetalheDialog: React.FC<GrupoDetalheDialogProps> = ({
  open, onOpenChange, grupoId, onAtualizado, onSaiu,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [saindo, setSaindo] = useState(false);
  const [resetandoLink, setResetandoLink] = useState(false);
  const [grupo, setGrupo] = useState<Grupo | null>(null);
  const [novosParticipantes, setNovosParticipantes] = useState('');
  const [adicionando, setAdicionando] = useState(false);
  const [participanteEmAcao, setParticipanteEmAcao] = useState<string | null>(null);
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoEntrada[]>([]);
  const [solicitacaoEmAcao, setSolicitacaoEmAcao] = useState<string | null>(null);

  // Groups API (Meta): só nome/descrição/foto, remoção de participantes e aprovação de entradas
  const ehMeta = grupo?.tipoApi === 'META';

  // Formulário de configurações
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [isAnnounce, setIsAnnounce] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isJoinApprovalRequired, setIsJoinApprovalRequired] = useState(false);
  const [memberAddMode, setMemberAddMode] = useState<'admin_add' | 'all_member_add'>('admin_add');
  const [ephemeralDuration, setEphemeralDuration] = useState<'0' | '1d' | '7d' | '90d'>('0');

  const carregarGrupo = async (id: string) => {
    setLoading(true);
    try {
      const response = await grupoService.obter(id, true);
      const g: Grupo = response.data.grupo;
      setGrupo(g);
      setSolicitacoes(response.data.solicitacoes || []);
      setNome(g.nome);
      setDescricao(g.descricao || '');
      setIsAnnounce(g.isAnnounce);
      setIsLocked(g.isLocked);
      setIsJoinApprovalRequired(g.isJoinApprovalRequired);
      setMemberAddMode((g.memberAddMode as any) || 'admin_add');
      setEphemeralDuration(inferirDuracaoInicial(g));
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar grupo',
        description: error?.response?.data?.message || 'Não foi possível carregar os dados do grupo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && grupoId) {
      carregarGrupo(grupoId);
    } else {
      setGrupo(null);
      setSolicitacoes([]);
      setNovosParticipantes('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, grupoId]);

  const handleSalvarConfiguracoes = async () => {
    if (!grupo) return;

    const payload: AtualizarGrupoData = {};
    if (nome.trim() && nome.trim() !== grupo.nome) payload.nome = nome.trim();
    if (descricao !== (grupo.descricao || '')) payload.descricao = descricao;
    if (!ehMeta) {
      if (isAnnounce !== grupo.isAnnounce) payload.isAnnounce = isAnnounce;
      if (isLocked !== grupo.isLocked) payload.isLocked = isLocked;
      if (isJoinApprovalRequired !== grupo.isJoinApprovalRequired) payload.isJoinApprovalRequired = isJoinApprovalRequired;
      if (memberAddMode !== (grupo.memberAddMode || 'admin_add')) payload.memberAddMode = memberAddMode;
      if (ephemeralDuration !== inferirDuracaoInicial(grupo)) payload.ephemeralDuration = ephemeralDuration;
    }

    if (Object.keys(payload).length === 0) {
      toast({ title: 'Nada para salvar', description: 'Nenhuma alteração foi feita.' });
      return;
    }

    setSalvando(true);
    try {
      const response = await grupoService.atualizar(grupo.id, payload);
      const atualizado: Grupo = response.data.grupo;
      setGrupo(atualizado);
      onAtualizado(atualizado);
      toast({ title: 'Grupo atualizado', description: 'As configurações foram salvas com sucesso.' });
    } catch (error: any) {
      toast({
        title: 'Erro ao salvar',
        description: error?.response?.data?.message || 'Não foi possível salvar as alterações.',
        variant: 'destructive',
      });
    } finally {
      setSalvando(false);
    }
  };

  const handleResetarLink = async () => {
    if (!grupo) return;
    setResetandoLink(true);
    try {
      await grupoService.resetarLinkConvite(grupo.id);
      await carregarGrupo(grupo.id);
      toast({ title: 'Link renovado', description: 'O link de convite anterior foi invalidado.' });
    } catch (error: any) {
      toast({
        title: 'Erro ao renovar link',
        description: error?.response?.data?.message || 'Não foi possível gerar um novo link.',
        variant: 'destructive',
      });
    } finally {
      setResetandoLink(false);
    }
  };

  const handleCopiarLink = () => {
    if (!grupo?.inviteLink) return;
    navigator.clipboard.writeText(grupo.inviteLink);
    toast({ title: 'Link copiado', description: 'O link de convite foi copiado para a área de transferência.' });
  };

  const handleAcaoParticipante = async (action: 'remove' | 'promote' | 'demote', jid: string) => {
    if (!grupo) return;
    setParticipanteEmAcao(jid);
    try {
      const response = await grupoService.alterarParticipantes(grupo.id, action, [jid]);
      const atualizado: Grupo = response.data.grupo;
      setGrupo(atualizado);
      onAtualizado(atualizado);
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar participante',
        description: error?.response?.data?.message || 'Não foi possível concluir a ação.',
        variant: 'destructive',
      });
    } finally {
      setParticipanteEmAcao(null);
    }
  };

  const handleSolicitacao = async (action: 'approve' | 'reject', joinRequestId: string) => {
    if (!grupo) return;
    setSolicitacaoEmAcao(joinRequestId);
    try {
      const response = await grupoService.alterarParticipantes(grupo.id, action, [joinRequestId]);
      const atualizado: Grupo = response.data.grupo;
      setGrupo(atualizado);
      onAtualizado(atualizado);
      setSolicitacoes((prev) => prev.filter((s) => s.joinRequestId !== joinRequestId));
      toast({ title: action === 'approve' ? 'Solicitação aprovada' : 'Solicitação rejeitada' });
    } catch (error: any) {
      toast({
        title: 'Erro ao processar solicitação',
        description: error?.response?.data?.message || 'Não foi possível concluir a ação.',
        variant: 'destructive',
      });
    } finally {
      setSolicitacaoEmAcao(null);
    }
  };

  const handleAdicionarParticipantes = async () => {
    if (!grupo) return;
    const numeros = parseParticipantes(novosParticipantes);
    if (numeros.length === 0) {
      toast({ title: 'Aviso', description: 'Informe ao menos um número válido', variant: 'destructive' });
      return;
    }
    setAdicionando(true);
    try {
      const response = await grupoService.alterarParticipantes(grupo.id, 'add', numeros);
      const atualizado: Grupo = response.data.grupo;
      setGrupo(atualizado);
      onAtualizado(atualizado);
      setNovosParticipantes('');
      toast({ title: 'Participantes adicionados' });
    } catch (error: any) {
      toast({
        title: 'Erro ao adicionar participantes',
        description: error?.response?.data?.message || 'Não foi possível adicionar os participantes.',
        variant: 'destructive',
      });
    } finally {
      setAdicionando(false);
    }
  };

  const handleSairDoGrupo = async () => {
    if (!grupo) return;
    setSaindo(true);
    try {
      await grupoService.sair(grupo.id);
      toast({
        title: ehMeta ? 'Grupo encerrado' : 'Você saiu do grupo',
        description: `"${grupo.nome}" não aparecerá mais na lista.`,
      });
      onSaiu(grupo.id);
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Erro ao sair do grupo',
        description: error?.response?.data?.message || 'Não foi possível sair do grupo.',
        variant: 'destructive',
      });
    } finally {
      setSaindo(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-800 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <UsersIcon className="h-5 w-5" />
            {grupo?.nome || 'Grupo'}
            {grupo?.suspenso && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                <Ban className="h-3 w-3" /> Suspenso
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !grupo ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="configuracoes" className="mt-2">
            <TabsList className="grid grid-cols-2 w-full bg-gray-200 dark:bg-slate-900 dark:border dark:border-slate-700">
              <TabsTrigger value="configuracoes" className="dark:text-gray-300 dark:data-[state=active]:bg-slate-600 dark:data-[state=active]:text-white">
                <SettingsIcon className="h-4 w-4 mr-2" /> Configurações
              </TabsTrigger>
              <TabsTrigger value="participantes" className="dark:text-gray-300 dark:data-[state=active]:bg-slate-600 dark:data-[state=active]:text-white">
                <UsersIcon className="h-4 w-4 mr-2" /> Participantes ({grupo.participantes?.length ?? grupo.totalParticipantes})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="configuracoes" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="dark:text-gray-200" htmlFor="edit-nome">Nome</Label>
                <Input className="dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400" id="edit-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={ehMeta ? 128 : 25} />
              </div>

              <div className="space-y-2">
                <Label className="dark:text-gray-200" htmlFor="edit-descricao">Descrição</Label>
                <Textarea className="dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400" id="edit-descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={3} maxLength={2048} />
              </div>

              {ehMeta && grupo.statusCriacao === 'pendente' && (
                <div className="flex gap-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 p-3 text-xs text-yellow-800 dark:text-yellow-200">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>A Meta ainda está criando este grupo. O link de convite será exibido assim que a criação for confirmada.</p>
                </div>
              )}
              {ehMeta && grupo.statusCriacao === 'falhou' && (
                <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-xs text-red-800 dark:text-red-200">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>Falha na criação do grupo: {grupo.erroCriacao || 'motivo não informado pela Meta'}. Você pode excluir este registro.</p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="dark:text-gray-200">Link de convite</Label>
                <div className="flex gap-2">
                  <Input readOnly value={grupo.inviteLink || 'Indisponível'} className="text-xs dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400" />
                  <Button type="button" variant="outline" size="icon" onClick={handleCopiarLink} disabled={!grupo.inviteLink} title="Copiar link">
                    <Copy className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="outline" size="icon" title="Gerar novo link" disabled={resetandoLink || (ehMeta && grupo.statusCriacao !== 'ativo')}>
                        {resetandoLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-white dark:bg-slate-800 dark:border-slate-700">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Gerar novo link de convite?</AlertDialogTitle>
                        <AlertDialogDescription>
                          O link atual deixará de funcionar imediatamente para quem ainda não entrou no grupo.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={handleResetarLink}>Gerar novo link</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {ehMeta ? (
                <div className="flex items-center justify-between rounded-lg border p-3 dark:border-slate-700">
                  <div>
                    <Label className="dark:text-gray-200">Entrada por aprovação</Label>
                    <p className="text-xs text-muted-foreground dark:text-gray-400">
                      {grupo.isJoinApprovalRequired
                        ? 'Ativada: novas entradas pelo link aguardam sua aprovação.'
                        : 'Desativada: quem abrir o link entra direto. Definido na criação do grupo e não pode ser alterado.'}
                    </p>
                  </div>
                  <Switch checked={grupo.isJoinApprovalRequired} disabled />
                </div>
              ) : (
                <>
              <div className="flex items-center justify-between rounded-lg border p-3 dark:border-slate-700">
                <div>
                  <Label className="dark:text-gray-200">Somente admins enviam mensagens</Label>
                  <p className="text-xs text-muted-foreground dark:text-gray-400">Restringe o envio de mensagens a administradores do grupo</p>
                </div>
                <Switch checked={isAnnounce} onCheckedChange={setIsAnnounce} />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3 dark:border-slate-700">
                <div>
                  <Label className="dark:text-gray-200">Bloquear edição das informações</Label>
                  <p className="text-xs text-muted-foreground dark:text-gray-400">Somente admins podem alterar nome, descrição e imagem</p>
                </div>
                <Switch checked={isLocked} onCheckedChange={setIsLocked} />
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3 dark:border-slate-700">
                <div>
                  <Label className="dark:text-gray-200">Exigir aprovação para entrar</Label>
                  <p className="text-xs text-muted-foreground dark:text-gray-400">Novas entradas pelo link precisam ser aprovadas</p>
                </div>
                <Switch checked={isJoinApprovalRequired} onCheckedChange={setIsJoinApprovalRequired} />
              </div>

              <div className="space-y-2">
                <Label className="dark:text-gray-200">Quem pode adicionar novos membros</Label>
                <Select value={memberAddMode} onValueChange={(v) => setMemberAddMode(v as any)}>
                  <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100">
                    <SelectItem value="admin_add">Somente administradores</SelectItem>
                    <SelectItem value="all_member_add">Qualquer participante</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="dark:text-gray-200">Mensagens temporárias</Label>
                <Select value={ephemeralDuration} onValueChange={(v) => setEphemeralDuration(v as any)}>
                  <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-gray-100">
                    <SelectItem value="0">Desativado</SelectItem>
                    <SelectItem value="1d">1 dia</SelectItem>
                    <SelectItem value="7d">7 dias</SelectItem>
                    <SelectItem value="90d">90 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
                </>
              )}

              <div className="flex justify-between items-center pt-2 border-t dark:border-slate-700">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="destructive" disabled={saindo}>
                      {saindo ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
                      {ehMeta ? (grupo.statusCriacao === 'ativo' ? 'Encerrar grupo' : 'Excluir registro') : 'Sair do grupo'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white dark:bg-slate-800 dark:border-slate-700">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{ehMeta ? 'Encerrar' : 'Sair de'} "{grupo.nome}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {ehMeta
                          ? 'O grupo será encerrado na API oficial do WhatsApp e todos os participantes serão removidos. Essa ação não pode ser desfeita.'
                          : 'A empresa deixará de fazer parte do grupo e não poderá mais enviar ou receber mensagens dele até ser adicionada novamente.'}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleSairDoGrupo} className="bg-red-600 hover:bg-red-700">{ehMeta ? 'Encerrar grupo' : 'Sair do grupo'}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <Button onClick={handleSalvarConfiguracoes} disabled={salvando} className="bg-green-600 hover:bg-green-700">
                  {salvando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Salvar alterações
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="participantes" className="space-y-4 py-4">
              {ehMeta ? (
                <div className="flex gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-800 dark:text-blue-200">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    Na API oficial, os participantes entram pelo link de convite (limite de {META_LIMITE_PARTICIPANTES} por grupo:
                    {' '}{grupo.totalParticipantes}/{META_LIMITE_PARTICIPANTES}). Aqui você pode removê-los e
                    {grupo.isJoinApprovalRequired ? ' aprovar ou rejeitar solicitações de entrada.' : ' acompanhar quem está no grupo.'}
                  </p>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Textarea
                    value={novosParticipantes}
                    onChange={(e) => setNovosParticipantes(e.target.value)}
                    placeholder={'Adicionar participantes: um número por linha'}
                    rows={2}
                    className="flex-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
                  />
                  <Button onClick={handleAdicionarParticipantes} disabled={adicionando} className="self-end">
                    {adicionando ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  </Button>
                </div>
              )}

              {ehMeta && solicitacoes.length > 0 && (
                <div className="space-y-1 rounded-lg border p-2 dark:border-slate-700">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 px-1">
                    Solicitações de entrada ({solicitacoes.length})
                  </p>
                  {solicitacoes.map((sol) => (
                    <div key={sol.joinRequestId} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <p className="text-sm dark:text-gray-200">{formatJid(sol.waId)}</p>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 text-green-600"
                          title="Aprovar"
                          disabled={solicitacaoEmAcao === sol.joinRequestId}
                          onClick={() => handleSolicitacao('approve', sol.joinRequestId)}
                        >
                          {solicitacaoEmAcao === sol.joinRequestId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 text-red-600"
                          title="Rejeitar"
                          disabled={solicitacaoEmAcao === sol.joinRequestId}
                          onClick={() => handleSolicitacao('reject', sol.joinRequestId)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-1 max-h-[360px] overflow-y-auto">
                {(grupo.participantes || []).map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-gray-300 dark:bg-slate-600 text-xs">{getIniciais(p.nome, p.jid)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate dark:text-gray-200">{p.nome || formatJid(p.jid)}</p>
                        <p className="text-xs text-muted-foreground dark:text-gray-400 truncate">{formatJid(p.jid)}</p>
                      </div>
                      {p.isSuperAdmin && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                          <ShieldCheck className="h-3 w-3" /> Criador
                        </span>
                      )}
                      {p.isAdmin && !p.isSuperAdmin && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                          <Shield className="h-3 w-3" /> Admin
                        </span>
                      )}
                    </div>

                    {!p.isSuperAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" disabled={participanteEmAcao === p.jid}>
                            {participanteEmAcao === p.jid ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="dark:bg-slate-800">
                          {!ehMeta && (p.isAdmin ? (
                            <DropdownMenuItem onClick={() => handleAcaoParticipante('demote', p.jid)}>
                              <Shield className="h-4 w-4 mr-2" /> Remover como admin
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleAcaoParticipante('promote', p.jid)}>
                              <ShieldCheck className="h-4 w-4 mr-2" /> Promover a admin
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuItem onClick={() => handleAcaoParticipante('remove', p.jid)} className="text-red-600 dark:text-red-400">
                            <UserMinus className="h-4 w-4 mr-2" /> Remover do grupo
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ))}
                {(!grupo.participantes || grupo.participantes.length === 0) && (
                  <p className="text-sm text-center text-muted-foreground py-6">Nenhum participante carregado.</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GrupoDetalheDialog;
